<template>
  <FaultGrabWaveformButton
    :is-superadmin="isSuperadmin"
    :is-download-access="isDownloadAccess"
    :sending-request="sendingRequest"
    @click.native="grabForWaveform"
  >
    <template>
      <div>{{ buttonMessage }}</div>
    </template>
  </FaultGrabWaveformButton>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import Guard1000LastWaveform from '@/resources/guard-1000-fault-last-waveform';
import FaultGrabWaveformButton from '@/components/shared/fault-grab-waveform-button';
import UsersMethods from '@/components/mixins/users-methods';
import { mappedCurrentNames } from '@/components/shared/guard-1000-consts';

const EVERY_10_SECOND_INTERVAL_IN_MS = 10 * 1000;
const ONE_MINUTE_SPAN = 60 * 1000;
const BUTTON_STATES = {
  START: 'Grab Waveform',
  FIRST_FETCH: 'Sending Request',
  WAITING: 'Waiting for data',
  FOUND: 'New waveform found'
};

export default {
  name: 'FaultGrabWaveform',
  components: { FaultGrabWaveformButton },
  mixins: [UsersMethods],
  data() {
    return {
      sendingRequest: false,
      buttonMessage: BUTTON_STATES.START,
      intervalId: null,
      intervalSpan: null,
      error: null
    };
  },
  computed: mapState({
    latestWaveformId: state =>
      state.guard1000FaultWaveforms.latestWaveform &&
      state.guard1000FaultWaveforms.latestWaveform.id
  }),
  beforeDestroy() {
    this.clearGrabWaveformIntervals();
  },
  methods: {
    ...mapMutations({
      setLatestWaveform: 'guard1000FaultWaveforms/setLatestWaveform'
    }),
    ...mapActions({
      grabWaveform: 'guard1000FaultWaveforms/grabWaveform',
      fetchWaveforms: 'guard1000FaultWaveforms/fetchPage',
      fetchEvents: 'guard1000FaultEvents/fetchPage'
    }),
    async grabForWaveform() {
      if (this.intervalId || this.intervalSpan) this.clearGrabWaveformIntervals();

      this.sendingRequest = true;

      const { projectCode, substationCode, externalGuardId } = this.$route.params;

      this.changeButtonState(BUTTON_STATES.FIRST_FETCH);

      await this.grabWaveform({ projectCode, substationCode, externalGuardId });

      this.watchForDataInInterval();
    },
    watchForDataInInterval() {
      this.checkIfNewWaveform();

      this.changeButtonState(BUTTON_STATES.WAITING);

      this.intervalId = setInterval(() => {
        this.checkIfNewWaveform();
      }, EVERY_10_SECOND_INTERVAL_IN_MS);

      this.intervalSpan = setTimeout(() => {
        this.sendingRequest = false;
        this.changeButtonState(BUTTON_STATES.START);

        this.clearGrabWaveformIntervals();
      }, ONE_MINUTE_SPAN);
    },
    async checkIfNewWaveform() {
      const {
        projectCode,
        substationCode,
        externalGuardId,
        feederNumber,
        phaseNumber
      } = this.$route.params;

      try {
        const lastWaveform = await Guard1000LastWaveform({
          externalGuardId
        }).get({
          projectCode,
          substationCode
        });

        if (!lastWaveform) {
          return Error('Response Error');
        }

        if (lastWaveform.body.data.id !== this.latestWaveformId) {
          this.changeButtonState(BUTTON_STATES.FOUND);

          const defaultParams = {
            options: { externalGuardId },
            projectCode,
            substationCode,
            page: { number: 1, size: 5 },
            data_type: mappedCurrentNames
          };

          this.fetchEvents({ ...defaultParams, feederNumber, phaseNumber });
          await this.fetchWaveforms(defaultParams);

          this.setLatestWaveform();

          this.clearGrabWaveformIntervals();
          this.sendingRequest = false;
          this.changeButtonState(BUTTON_STATES.START);
        }
      } catch (responseError) {
        this.error = responseError;
      }
    },
    changeButtonState(buttonState) {
      this.buttonMessage = buttonState;
    },
    clearGrabWaveformIntervals() {
      clearInterval(this.intervalSpan);
      clearInterval(this.intervalId);

      this.intervalSpan = null;
      this.intervalId = null;
    }
  }
};
</script>
